import { sectionRenderer, normalizeSectionId } from "./pent-core/index.js"
import { dispatchCartUpdated } from "./pent-core/events.js"

/**
 * PlCartPage - Custom element for cart page
 * Handles cart item removal and updates on the cart page
 */
class PlCartPage extends HTMLElement {
  connectedCallback() {
    this.#attachCartListeners()
  }

  #attachCartListeners() {
    // Handle remove item clicks (use event delegation for dynamic content)
    this.addEventListener("click", (event) => {
      const removeBtn = event.target.closest("[data-cart-remove]")
      if (removeBtn) {
        const lineKey = removeBtn.dataset.cartRemove
        if (lineKey) {
          this.#removeItem(lineKey, removeBtn)
        }
      }
    })

    // Listen for cart:updated events to refresh the page
    document.addEventListener("cart:updated", this.#handleCartUpdated.bind(this))
  }

  /**
   * Removes an item from the cart
   * @param {string} lineKey - The cart line item key
   * @param {HTMLElement} [triggerElement] - The element that triggered the removal
   */
  async #removeItem(lineKey, triggerElement) {
    // Optimistic UI: Hide the line item immediately
    if (triggerElement) {
      const lineItem = triggerElement.closest(".pl--cart-drawer-line-item")
      if (lineItem) {
        lineItem.style.display = "none"
      }
    }

    try {
      const url = (window.Shopify?.routes?.root || "/") + "cart/change.js"

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
        }),
      })

      if (!response.ok) {
        console.error("Failed to remove item:", response.status)

        // Revert optimistic update on error
        if (triggerElement) {
          const lineItem = triggerElement.closest(".pl--cart-drawer-line-item")
          if (lineItem) {
            lineItem.style.display = ""
          }
        }
        return
      }

      const cart = await response.json()

      // Dispatch cart:updated event
      dispatchCartUpdated(document, { cart })
    } catch (error) {
      console.error("Failed to remove item:", error)
    }
  }

  async #handleCartUpdated(event) {
    const { cart } = event.detail

    // Find section ID
    const sectionElement = this.closest(".shopify-section")
    const sectionId = sectionElement?.id

    if (sectionId) {
      // Normalize the section ID (remove shopify-section- prefix if present)
      const normalizedId = normalizeSectionId(sectionId)
      // Re-render to update cart items
      await this.#updateCartContent(normalizedId)
    }
  }

  async #updateCartContent(sectionId) {
    try {
      await sectionRenderer.renderSection(sectionId, {
        cache: false,
      })
    } catch (error) {
      console.error("Failed to update cart page:", error)
    }
  }
}

if (!customElements.get("pl-cart-page")) {
  customElements.define("pl-cart-page", PlCartPage)
}

export { PlCartPage }
